import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Check, Loader2, CheckCircle2, CalendarClock } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import Seo from "@/components/Seo";
import DateField from "@/components/DateField";
import { vehicleLabel } from "@/lib/utils";
import SearchSelect from "@/crm/components/SearchSelect";
import { api } from "@/lib/api";
import { fmtMoney, promoActive, promoPrice, promoPctFor } from "@/lib/promo";
import PpfBuilder from "@/components/PpfBuilder";
import { track } from "@/lib/analytics";
import { BOOKING_CONFIRMATION_KEY } from "@/pages/BookingConfirmed";

const sel = "w-full bg-white/5 border border-white/12 rounded-lg px-3 py-2.5 outline-none focus:border-[#1F5EFF] text-white text-sm";
const inp = sel;

const YEARS = Array.from({ length: 33 }, (_, i) => String(2027 - i));
const MAX_SERVICES = 4;

// Short fine print on service cards — mirrors the service-page wording.
// Copy only; prices and availability come from the server config.
const SERVICE_NOTES = {
  "ceramic-3yr": "Wash, decontamination & light one-step correction included · may stay overnight for curing",
  "ceramic-5yr": "Wash, decontamination & light one-step correction included · may stay overnight for curing",
  "ceramic-7yr": "Wash, decontamination & light one-step correction included · may stay overnight for curing",
  "interior-detail": "Heavy soiling of headliner/seats may add a surcharge",
  "full-detail": "Heavy soiling of headliner/seats may add a surcharge",
};

// Slim ticking countdown to the promo end date — urgency without a banner wall.
function PromoCountdown({ promo }) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);
  if (!promoActive(promo) || !promo?.expires_at) return null;
  const left = new Date(`${promo.expires_at}T23:59:59`).getTime() - now;
  if (left <= 0) return null;
  const d = Math.floor(left / 86400000);
  const h = Math.floor(left / 3600000) % 24;
  const m = Math.floor(left / 60000) % 60;
  const s = Math.floor(left / 1000) % 60;
  return (
    <div className="mt-6 inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-emerald-500/8 border border-emerald-500/25" data-testid="qb-promo-countdown">
      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
      <span className="font-mono-alt text-[12px] text-emerald-300">
        {promo.badge || "Grand Opening"} pricing ends in {d}d {String(h).padStart(2, "0")}h {String(m).padStart(2, "0")}m {String(s).padStart(2, "0")}s
      </span>
    </div>
  );
}

function fromInfo(svc, promo) {
  let min = null;
  if (svc.options?.length) min = Math.min(...svc.options.map((o) => o.price));
  else if ((svc.price_kind || "").startsWith("flat")) min = svc.flat_price;
  else {
    const p = Object.values(svc.prices || {}).filter((v) => v != null);
    min = p.length ? Math.min(...p) : null;
  }
  if (min == null) return { from: null, promoFrom: null };
  const eligible = promoActive(promo) && (promo.services || []).includes(svc.key);
  return { from: min, promoFrom: eligible ? promoPrice(min, promoPctFor(promo, svc.key)) : null };
}

// Full Interior + Exterior saving vs booking both separately — computed from
// canonical config prices (uniform across classes), never hard-coded.
function fullDetailSaving(config) {
  const find = (k) => (config?.services || []).find((s) => s.key === k);
  const i = find("interior-regular") || find("interior-detail"), e = find("exterior-detail"), f = find("full-detail");
  if (!i || !e || !f) return 0;
  const savings = Object.keys(f.prices || {})
    .map((c) => (i.prices?.[c] != null && e.prices?.[c] != null && f.prices?.[c] != null)
      ? i.prices[c] + e.prices[c] - f.prices[c] : null)
    .filter((v) => v != null && v > 0);
  return savings.length ? Math.min(...savings) : 0;
}

export default function QuickBook() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);

  // Step 1 — services
  const [config, setConfig] = useState(null);
  const [serviceKeys, setServiceKeys] = useState([]);
  const [serviceOptions, setServiceOptions] = useState({});
  // PPF mode — date-only request flow (no time slot; shop schedules drop-off)
  const [ppfOpen, setPpfOpen] = useState(false);
  const [ppfSel, setPpfSel] = useState(null);   // {package, removed_parts, extra_parts, windshield}
  const [ppfQuote, setPpfQuote] = useState(null);
  const [ppfInitial, setPpfInitial] = useState(null);

  // Step 2 — vehicle + schedule
  const [year, setYear] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [modelsLoading, setModelsLoading] = useState(false);
  const [classified, setClassified] = useState(null);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [slots, setSlots] = useState(null);
  const [slotsLoading, setSlotsLoading] = useState(false);

  // Step 3 — contact
  const [form, setForm] = useState({ name: "", phone: "", email: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const bookingCfg = config?.booking || {};
  const todayStr = new Date().toLocaleDateString("en-CA");
  const maxDateStr = new Date(Date.now() + (bookingCfg.horizon_days || 30) * 864e5).toLocaleDateString("en-CA");
  const minDateStr = bookingCfg.min_date && bookingCfg.min_date > todayStr ? bookingCfg.min_date : todayStr;

  useEffect(() => {
    api.get("/pricing/config").then(({ data }) => setConfig(data)).catch(() => {});
    api.get("/vehicles/makes").then(({ data }) => setMakes(data)).catch(() => {});
    // Handoff from the calculator's PPF builder (vehicle + selection pre-filled)
    try {
      const pre = JSON.parse(sessionStorage.getItem("wtd_ppf_prefill") || "null");
      if (pre && pre.selection) {
        sessionStorage.removeItem("wtd_ppf_prefill");
        if (pre.vehicle?.year) setYear(String(pre.vehicle.year));
        if (pre.vehicle?.make) setMake(pre.vehicle.make);
        if (pre.vehicle?.model) setModel(pre.vehicle.model);
        setPpfInitial(pre.selection);
        setPpfSel(pre.selection);
        setPpfOpen(true);
      }
    } catch { /* no prefill */ }
  }, []);

  // models for chosen year+make
  useEffect(() => {
    setModels([]);
    if (!year || !make) { setModel(""); return; }
    setModelsLoading(true);
    api.get("/vehicles/models", { params: { year, make } })
      .then(({ data }) => { setModels(data); setModel((m) => (m && data.includes(m) ? m : "")); })
      .catch(() => setModels([]))
      .finally(() => setModelsLoading(false));
  }, [year, make]);

  // classify when vehicle complete
  useEffect(() => {
    setClassified(null);
    if (!year || !make || !model) return;
    api.post("/vehicles/classify", { year: Number(year), make, model })
      .then(({ data }) => setClassified(data))
      .catch(() => setClassified(null));
  }, [year, make, model]);

  const selectedServices = useMemo(
    () => (config ? serviceKeys.map((k) => config.services.find((s) => s.key === k)).filter(Boolean) : []),
    [config, serviceKeys]
  );

  const toggleService = (key) => {
    // PPF is booked through its own date-request flow — never mixed with
    // detailing services in one booking.
    setPpfOpen(false);
    setPpfSel(null);
    setPpfQuote(null);
    setServiceKeys((prev) => {
      const next = prev.includes(key)
        ? prev.filter((k) => k !== key)
        : prev.length >= MAX_SERVICES ? prev : [...prev, key];
      return next;
    });
    setDate("");
    setTime("");
  };

  // default option for option-priced services; prune deselected
  useEffect(() => {
    setServiceOptions((o) => {
      const next = {};
      for (const k of serviceKeys) {
        const svc = config?.services.find((s) => s.key === k);
        if (svc?.options?.length) next[k] = o[k] && svc.options.some((op) => op.key === o[k]) ? o[k] : svc.options[0].key;
      }
      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serviceKeys, config]);

  // server-authoritative base quote (no add-ons) once vehicle + services known
  useEffect(() => {
    setQuote(null);
    if (!year || !make || !model || serviceKeys.length === 0) return;
    let cancelled = false;
    setQuoteLoading(true);
    api.post("/pricing/quote", {
      year: Number(year), make, model,
      service_keys: serviceKeys,
      service_options: serviceOptions,
    })
      .then(({ data }) => { if (!cancelled) setQuote(data); })
      .catch(() => { if (!cancelled) setQuote(null); })
      .finally(() => { if (!cancelled) setQuoteLoading(false); });
    return () => { cancelled = true; };
  }, [year, make, model, serviceKeys, serviceOptions]);

  // real availability once a date is picked
  useEffect(() => {
    if (!date || serviceKeys.length === 0) { setSlots(null); return; }
    let cancelled = false;
    setSlotsLoading(true);
    setTime("");
    api.get("/booking/slots", { params: { date, service_keys: serviceKeys.join(",") } })
      .then(({ data }) => { if (!cancelled) setSlots(data); })
      .catch(() => { if (!cancelled) setSlots(null); })
      .finally(() => { if (!cancelled) setSlotsLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, serviceKeys]);

  const vehicleReady = year && make && model;
  const ppfMode = Boolean(ppfOpen);
  const ppfHasSelection = Boolean(ppfSel && (ppfSel.package || ppfSel.windshield || (ppfSel.extra_parts || []).length > 0));
  const step2Ready = ppfMode ? Boolean(vehicleReady && date && ppfHasSelection) : Boolean(vehicleReady && date && time);
  const customPrice = quote && quote.total == null;

  const togglePpf = () => {
    setPpfOpen((open) => {
      const next = !open;
      if (next) { setServiceKeys([]); setDate(""); setTime(""); }
      else { setPpfSel(null); setPpfQuote(null); }
      return next;
    });
  };

  const submit = async () => {
    setError("");
    if (!form.name.trim()) { setError("Please enter your name"); return; }
    if (form.phone.trim().length < 4) { setError("Please enter your phone"); return; }
    if (!/.+@.+\..+/.test(form.email.trim())) { setError("Please enter a valid email"); return; }
    setBusy(true);
    try {
      if (ppfMode) {
        // PPF date-request: no time slot; the shop schedules drop-off manually
        const { data } = await api.post("/booking/book-ppf", {
          name: form.name.trim(),
          phone: form.phone.trim(),
          email: form.email.trim(),
          message: form.message.trim() || undefined,
          year: Number(year), make, model,
          ppf: ppfSel,
          date,
        });
        const confirmation = {
          ref: data.quote_id,
          service_name: data.service_name,
          date: data.date,
          time: null,
          duration_min: null,
          total: data.total,
          price_type: data.price_type,
          vehicle: `${year} ${make} ${model}`,
          services: (ppfQuote?.lines || []).map((l) => ({ name: l.label, price: l.amount })),
          addons: [],
          savings: 0,
        };
        try { sessionStorage.setItem(BOOKING_CONFIRMATION_KEY, JSON.stringify(confirmation)); } catch { /* router state still works */ }
        track("booking_completed", {
          booked_with_slot: false,
          flow: "quick_book_ppf",
          primary_service: ppfSel?.package || "ppf-custom",
          services_count: (ppfQuote?.lines || []).length,
          total: data.total ?? null,
        });
        navigate("/booking-confirmed", { state: confirmation });
        return;
      }
      const { data } = await api.post("/booking/book", {
        name: form.name.trim(),
        phone: form.phone.trim(),
        email: form.email.trim(),
        message: form.message.trim() || undefined,
        year: Number(year), make, model,
        service_keys: serviceKeys,
        service_options: serviceOptions,
        date, time,
      });
      const confirmation = {
        ref: data.appointment_id,
        service_name: data.service_name,
        date: data.date,
        time: data.time,
        duration_min: data.duration_min,
        total: data.total,
        price_type: data.price_type,
        vehicle: `${year} ${make} ${model}`,
        services: quote?.services || null,
        addons: [],
        savings: quote?.discounts?.total_discount || 0,
      };
      try { sessionStorage.setItem(BOOKING_CONFIRMATION_KEY, JSON.stringify(confirmation)); } catch { /* router state still works */ }
      track("booking_completed", {
        booked_with_slot: true,
        flow: "quick_book",
        primary_service: serviceKeys[0] || null,
        services_count: serviceKeys.length,
        total: data.total ?? null,
      });
      navigate("/booking-confirmed", { state: confirmation });
    } catch (e) {
      setError(e?.response?.data?.detail || "Could not complete the booking — please try another slot or call us.");
      setBusy(false);
    }
  };

  const steps = ppfMode ? ["Services", "Vehicle & Date", "Your Details"] : ["Services", "Vehicle & Time", "Your Details"];

  return (
    <div className="min-h-screen bg-[#0c0c0f] text-white" data-testid="quick-book-page">
      <Seo
        title="Book Online in 3 Steps — WTD Detailing & Tint Edmonton"
        description="Quick online booking: pick your services, choose a slot, done. Exact base price shown instantly — no add-on maze."
        path="/book"
      />
      <Navbar />
      <main className="max-w-3xl mx-auto px-6 pt-32 pb-24">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-8 h-px bg-[#1F5EFF]" />
          <span className="font-mono-alt text-[12px] text-[#1F5EFF]">Quick Booking</span>
        </div>
        <h1 className="font-display text-4xl md:text-5xl font-light tracking-tight leading-[1.05]">
          Book in 3 steps.
        </h1>
        <p className="mt-4 text-white/60 leading-relaxed">
          Pick services, choose a slot, leave your contacts — we confirm shortly.{" "}
          Want an exact price with all options &amp; packages?{" "}
          <Link to="/calculator" className="text-[#8db2ff] hover:text-white transition-colors" data-testid="qb-calculator-link">
            Open the full calculator →
          </Link>
        </p>

        {/* PPF / Windshield are excluded from Grand Opening — no promo countdown in that flow */}
        {!ppfMode && <PromoCountdown promo={config?.promo} />}

        {/* Stepper */}
        <div className="mt-10 flex items-center gap-2" data-testid="qb-stepper">
          {steps.map((s, i) => (
            <React.Fragment key={s}>
              <div className={`flex items-center gap-2 ${step === i + 1 ? "text-white" : step > i + 1 ? "text-[#8db2ff]" : "text-white/35"}`}>
                <span className={`w-6 h-6 rounded-full border flex items-center justify-center text-[11px] font-mono-alt ${
                  step > i + 1 ? "bg-[#1F5EFF] border-[#1F5EFF]" : step === i + 1 ? "border-[#1F5EFF]" : "border-white/20"
                }`}>
                  {step > i + 1 ? <Check className="w-3 h-3" /> : i + 1}
                </span>
                <span className="text-xs hidden sm:inline">{s}</span>
              </div>
              {i < steps.length - 1 && <div className="flex-1 h-px bg-white/10" />}
            </React.Fragment>
          ))}
        </div>

        <motion.div key={step} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}
          className="mt-8 bg-[#121216] border border-white/10 rounded-2xl p-6 md:p-8">

          {/* STEP 1 — services */}
          {step === 1 && (
            <div data-testid="qb-step-1">
              <div className="font-mono-alt text-[10px] text-white/40 mb-4 uppercase tracking-widest">What does your vehicle need?</div>
              {!config && <div className="text-white/40 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Loading services…</div>}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                {(config?.services || []).filter((s) => s.listed !== false).map((s) => {
                  const active = serviceKeys.includes(s.key);
                  const fi = fromInfo(s, config?.promo);
                  const fdSave = s.key === "full-detail" ? fullDetailSaving(config) : 0;
                  return (
                    <button key={s.key} type="button" onClick={() => toggleService(s.key)} data-testid={`qb-service-${s.key}`}
                      className={`text-left p-4 rounded-xl border transition-colors ${active ? "border-[#1F5EFF] bg-[#1F5EFF]/10" : "border-white/10 bg-white/5 hover:border-white/25"}`}>
                      <div className="flex items-start justify-between gap-2">
                        <span className="text-sm text-white font-medium flex-1">{s.name}</span>
                        {fi.promoFrom != null && (
                          <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300 text-[10px] font-mono-alt whitespace-nowrap" data-testid={`qb-promo-badge-${s.key}`}>
                            {promoPctFor(config.promo, s.key)}% OFF
                          </span>
                        )}
                        {active && <CheckCircle2 className="w-4 h-4 text-[#8db2ff] shrink-0" />}
                      </div>
                      {s.subtitle && (
                        <div className="mt-1 text-[10px] font-mono-alt uppercase tracking-widest text-white/40" data-testid={`qb-subtitle-${s.key}`}>
                          {s.subtitle}
                        </div>
                      )}
                      {SERVICE_NOTES[s.key] && (
                        <div className="mt-1 text-[10px] text-white/35 leading-snug" data-testid={`qb-note-${s.key}`}>
                          {SERVICE_NOTES[s.key]}
                        </div>
                      )}
                      {fdSave > 0 && (
                        <div className="mt-1.5 flex flex-wrap gap-1.5">
                          <span className="px-1.5 py-0.5 rounded bg-[#1F5EFF]/15 border border-[#1F5EFF]/40 text-[#8db2ff] text-[9px] font-mono-alt uppercase tracking-widest" data-testid="qb-best-value-full-detail">Best Value</span>
                          <span className="px-1.5 py-0.5 rounded bg-emerald-500/15 border border-emerald-500/30 text-emerald-300 text-[9px] font-mono-alt uppercase tracking-widest" data-testid="qb-save-badge-full-detail">Save {fmtMoney(fdSave)}</span>
                        </div>
                      )}
                      <div className="text-xs font-mono-alt mt-1.5" data-testid={`qb-price-${s.key}`}>
                        {fi.from == null ? (
                          <span className="text-[#8db2ff]">Custom Quote</span>
                        ) : fi.from === 0 ? (
                          <span className="text-emerald-300">Free</span>
                        ) : fi.promoFrom != null ? (
                          <>
                            <span className="text-white/30 line-through mr-1.5">From {fmtMoney(fi.from)}</span>
                            <span className="text-emerald-300">From {fmtMoney(fi.promoFrom)}</span>
                          </>
                        ) : (
                          <span className="text-[#8db2ff]">From {fmtMoney(fi.from)}</span>
                        )}
                      </div>
                      {active && s.options?.length > 0 && (
                        <div className="mt-2.5 flex gap-1.5" onClick={(e) => e.stopPropagation()}>
                          {s.options.map((o) => (
                            <button key={o.key} type="button"
                              onClick={() => { setServiceOptions((prev) => ({ ...prev, [s.key]: o.key })); setDate(""); setTime(""); }}
                              data-testid={`qb-option-${s.key}-${o.key}`}
                              className={`px-2.5 py-1 rounded-full text-[11px] border transition-colors ${serviceOptions[s.key] === o.key ? "bg-[#1F5EFF] border-[#1F5EFF] text-white" : "border-white/15 text-white/60 hover:border-white/40"}`}>
                              {o.label || o.key} · {fmtMoney(o.price)}
                            </button>
                          ))}
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>

              {/* PPF / Windshield film — date-request flow (no time slot) */}
              <div className="mt-3">
                <button type="button" onClick={togglePpf} data-testid="qb-service-ppf"
                  className={`w-full text-left p-4 rounded-xl border transition-colors ${ppfOpen ? "border-[#1F5EFF] bg-[#1F5EFF]/10" : "border-white/10 bg-white/5 hover:border-white/25"}`}>
                  <div className="flex items-start justify-between gap-2">
                    <span className="text-sm text-white font-medium flex-1">Paint Protection Film &amp; Windshield Film</span>
                    <span className="px-1.5 py-0.5 rounded bg-[#1F5EFF]/15 border border-[#1F5EFF]/40 text-[#8db2ff] text-[9px] font-mono-alt uppercase tracking-widest">New</span>
                    {ppfOpen && <CheckCircle2 className="w-4 h-4 text-[#8db2ff] shrink-0" />}
                  </div>
                  <div className="mt-1 text-[10px] text-white/35 leading-snug" data-testid="qb-note-ppf">
                    Veltik PPF packages from $699 · individual panels · windshield protection film. Booked by preferred date — we confirm the exact drop-off time.
                  </div>
                  <div className="text-xs font-mono-alt mt-1.5">
                    <span className="text-[#8db2ff]">From $699</span>
                  </div>
                </button>
              </div>

              <div className="mt-6 flex items-center justify-between">
                <span className="text-white/40 text-xs" data-testid="qb-selected-count">
                  {ppfMode
                    ? (ppfHasSelection ? "PPF coverage selected" : "Pick your PPF coverage in the next step")
                    : serviceKeys.length === 0 ? "Select at least one service" : `${serviceKeys.length} service${serviceKeys.length > 1 ? "s" : ""} selected`}
                </span>
                <button onClick={() => setStep(2)} disabled={!ppfMode && serviceKeys.length === 0} data-testid="qb-next-1"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1F5EFF] text-white text-sm font-semibold hover:bg-white hover:text-black transition-colors disabled:opacity-40">
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 2 — vehicle + schedule */}
          {step === 2 && (
            <div data-testid="qb-step-2">
              <div className="font-mono-alt text-[10px] text-white/40 mb-3 uppercase tracking-widest">Your vehicle</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <SearchSelect value={year} onChange={(v) => { setYear(v); setMake(""); setModel(""); }} options={YEARS} placeholder="Year" testid="qb-year" />
                <SearchSelect value={make} onChange={(v) => { setMake(v); setModel(""); }} options={makes} placeholder="Make" disabled={!year} testid="qb-make" />
                <SearchSelect value={model} onChange={setModel} options={models} placeholder={modelsLoading ? "Loading…" : "Model"} disabled={!make || modelsLoading} loading={modelsLoading} testid="qb-model" />
              </div>
              {classified && (
                <div className="mt-3 inline-flex items-center gap-2 text-[12px] text-emerald-300" data-testid="qb-class-ok">
                  <CheckCircle2 className="w-3.5 h-3.5" />
                  Vehicle sized automatically — price below is exact for your {vehicleLabel(year, make, model)}
                </div>
              )}

              {/* PPF coverage builder (PPF mode only) */}
              {ppfMode && (
                <div className="mt-5" data-testid="qb-ppf-builder">
                  <div className="font-mono-alt text-[10px] text-white/40 mb-3 uppercase tracking-widest">Your PPF coverage</div>
                  <PpfBuilder
                    year={year} make={make} model={model}
                    vehicleClass={classified?.vehicle_class || null}
                    initial={ppfInitial}
                    onChange={(sel, q) => { setPpfSel(sel); setPpfQuote(q); }}
                    testid="qb-ppf"
                  />
                </div>
              )}

              {/* price panel */}
              {vehicleReady && !ppfMode && (
                <div className="mt-5 p-4 rounded-xl bg-white/3 border border-white/10" data-testid="qb-price-panel">
                  {quoteLoading && <div className="text-white/40 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Calculating…</div>}
                  {!quoteLoading && quote && (
                    <>
                      <div className="space-y-1.5">
                        {(quote.services || []).map((s) => (
                          <div key={s.key} className="flex justify-between text-sm">
                            <span className="text-white/70">{s.name}</span>
                            <span className="text-white font-mono-alt">{s.price != null ? fmtMoney(s.price) : "custom"}</span>
                          </div>
                        ))}
                      </div>
                      {quote.discounts?.total_discount > 0 && (
                        <div className="flex justify-between text-sm mt-2 pt-2 border-t border-white/10 text-emerald-300" data-testid="qb-savings">
                          <span>Grand Opening savings</span>
                          <span className="font-mono-alt">−{fmtMoney(quote.discounts.total_discount)}</span>
                        </div>
                      )}
                      <div className="flex justify-between mt-2 pt-2 border-t border-white/10">
                        <span className="text-white/60 text-sm">Total{customPrice ? "" : " (before optional add-ons)"}</span>
                        <span className="font-display text-xl text-white" data-testid="qb-total">
                          {customPrice ? "Custom quote" : (
                            <>
                              {quote.discounts?.total_discount > 0 && quote.discounts?.regular_subtotal != null && (
                                <span className="text-white/35 line-through text-sm mr-2 font-mono-alt">{fmtMoney(quote.discounts.regular_subtotal)}</span>
                              )}
                              {fmtMoney(quote.total)}
                            </>
                          )}
                        </span>
                      </div>
                      <div className="text-white/35 text-[11px] mt-1.5">
                        Exact base price — no add-ons. Want to customize? <Link to="/calculator" className="text-[#8db2ff] hover:text-white">Use the full calculator</Link> or add options at check-in.
                      </div>
                    </>
                  )}
                </div>
              )}

              <div className="mt-6 font-mono-alt text-[10px] text-white/40 mb-3 uppercase tracking-widest">
                {ppfMode ? "Preferred drop-off date" : "Pick a day & time"}
              </div>
              <DateField value={date} onChange={setDate} min={minDateStr} max={maxDateStr} testid="qb-date" />
              {ppfMode && date && (
                <div className="mt-3 text-[12px] text-white/50 leading-snug" data-testid="qb-ppf-date-note">
                  PPF installs are scheduled individually — we confirm the exact drop-off time and duration when we confirm your request.
                </div>
              )}
              {date && !ppfMode && (
                <div className="mt-4">
                  {slotsLoading && <div className="text-white/40 text-sm flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Checking availability…</div>}
                  {!slotsLoading && slots?.closed && (
                    <div className="text-amber-300/90 text-sm" data-testid="qb-closed">
                      {slots.reason === "before_opening"
                        ? `We open on ${slots.opens} — online booking is available from that day.`
                        : "This day is unavailable — please pick another date."}
                    </div>
                  )}
                  {!slotsLoading && slots && !slots.closed && (
                    <>
                      {slots.duration_min && (
                        <div className="text-white/40 text-xs mb-2 flex items-center gap-1.5">
                          <CalendarClock className="w-3.5 h-3.5" /> Approx. {Math.round(slots.duration_min / 60 * 10) / 10} h on site
                        </div>
                      )}
                      <div className="grid grid-cols-4 sm:grid-cols-5 gap-1.5" data-testid="qb-slots">
                        {slots.slots.map((s) => (
                          <button key={s.time} type="button" disabled={!s.available}
                            onClick={() => setTime(s.time)} data-testid={`qb-slot-${s.time}`}
                            className={`py-2 rounded-lg text-xs font-mono-alt border transition-colors ${
                              time === s.time ? "bg-[#1F5EFF] border-[#1F5EFF] text-white"
                              : s.available ? "border-white/12 text-white/70 hover:border-[#1F5EFF]/60"
                              : "border-white/5 text-white/20 cursor-not-allowed line-through"
                            }`}>
                            {s.time}
                          </button>
                        ))}
                      </div>
                      {!slots.slots.some((s) => s.available) && (
                        <div className="text-amber-300/90 text-sm mt-2">No free slots this day — try another date.</div>
                      )}
                    </>
                  )}
                </div>
              )}

              <div className="mt-6 flex items-center justify-between">
                <button onClick={() => setStep(1)} className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm" data-testid="qb-back-2">
                  <ArrowLeft className="w-4 h-4" /> Services
                </button>
                <button onClick={() => setStep(3)} disabled={!step2Ready} data-testid="qb-next-2"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#1F5EFF] text-white text-sm font-semibold hover:bg-white hover:text-black transition-colors disabled:opacity-40">
                  Continue <ArrowRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          )}

          {/* STEP 3 — contact */}
          {step === 3 && (
            <div data-testid="qb-step-3">
              <div className="font-mono-alt text-[10px] text-white/40 mb-3 uppercase tracking-widest">Your details</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Your name *" className={inp} data-testid="qb-name" maxLength={120} />
                <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone *" className={inp} data-testid="qb-phone" maxLength={40} />
              </div>
              <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} type="email" placeholder="Email *" className={inp + " mt-2.5"} data-testid="qb-email" />
              <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={2} placeholder="Anything we should know? (optional)" className={inp + " mt-2.5 resize-y"} data-testid="qb-message" maxLength={2000} />

              <div className="mt-5 p-4 rounded-xl bg-white/3 border border-white/10 text-sm space-y-1" data-testid="qb-summary">
                <div className="flex justify-between"><span className="text-white/50">Services</span><span className="text-white text-right">{ppfMode ? (ppfQuote?.lines || []).map((l) => l.label).join(" + ") : selectedServices.map((s) => s.name).join(" + ")}</span></div>
                <div className="flex justify-between"><span className="text-white/50">Vehicle</span><span className="text-white">{vehicleLabel(year, make, model)}</span></div>
                <div className="flex justify-between"><span className="text-white/50">When</span><span className="text-white">{ppfMode ? `${date} · time confirmed by us` : `${date} · ${time}`}</span></div>
                {ppfMode && ppfQuote && (
                  <div className="flex justify-between"><span className="text-white/50">GST (5%)</span><span className="text-white tabular-nums">{fmtMoney(ppfQuote.gst)}</span></div>
                )}
                <div className="flex justify-between pt-1.5 border-t border-white/10">
                  <span className="text-white/50">Total</span>
                  <span className="text-white font-display text-lg" data-testid="qb-summary-total">
                    {ppfMode
                      ? (ppfQuote ? `${fmtMoney(ppfQuote.total)}` : "—")
                      : customPrice ? "Custom quote" : quote ? (
                      <>
                        {quote.discounts?.total_discount > 0 && quote.discounts?.regular_subtotal != null && (
                          <span className="text-white/35 line-through text-xs mr-2 font-mono-alt">{fmtMoney(quote.discounts.regular_subtotal)}</span>
                        )}
                        {fmtMoney(quote.total)}
                      </>
                    ) : "—"}
                  </span>
                </div>
                {ppfMode && <div className="text-[10px] text-white/35">Includes GST. Final price confirmed at drop-off — PPF pricing is not combined with other promotions.</div>}
              </div>

              {error && <div className="mt-3 text-red-300 text-sm" data-testid="qb-error">{error}</div>}

              <div className="mt-6 flex items-center justify-between">
                <button onClick={() => setStep(2)} className="inline-flex items-center gap-2 text-white/50 hover:text-white text-sm" data-testid="qb-back-3">
                  <ArrowLeft className="w-4 h-4" /> Schedule
                </button>
                <button onClick={submit} disabled={busy} data-testid="qb-submit"
                  className="inline-flex items-center gap-2 px-7 py-3 rounded-full bg-[#1F5EFF] text-white text-sm font-semibold hover:bg-white hover:text-black transition-colors disabled:opacity-40">
                  {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Confirm Booking
                </button>
              </div>
            </div>
          )}
        </motion.div>
      </main>
      <Footer />
    </div>
  );
}
